import React, {createContext, useContext, useState, useEffect, useRef} from "react";
import api from "./api";
import {safeStorage} from "./errorReporting";

const TranslationContext = createContext();

const DOMAIN_LANGUAGE_MAP = {
    'localhost': 'nl',
  'oogst.app': 'nl',
  'oogst.local': 'nl',
  'gemusegartenplaner.de': 'de',
  'gemusegartenplaner.local': 'de',
  'recolte.app': 'fr',
  'recolte.local': 'fr',
  'plucking.app': 'en',
  'plucking.local': 'en',
};


export const TranslationProvider = ({children}) => {

    const detectBrowserLanguage = () => {
        // Get browser language
        const browserLang = navigator.language || navigator.userLanguage;
        // Extract the language code (e.g., 'en-US' -> 'en', 'nl-NL' -> 'nl')
        const langCode = browserLang.toLowerCase().substring(0, 2);

        // Support Dutch, English, French, and German, default to Dutch for this Dutch app
        const supportedLanguages = ['nl', 'en', 'fr', 'de'];
        return supportedLanguages.includes(langCode) ? langCode : 'nl';
    };

    // Priority: company language (for logged-in users) > localStorage > SSR > domain > browser detection
    const ssrLanguage = typeof window !== 'undefined' ? window.__INITIAL_LANGUAGE__ : null;

    // Check domain for default language
    const hostname = typeof window !== 'undefined' ? window.location.hostname : null;
    let domainLanguage = null;
    if (hostname && DOMAIN_LANGUAGE_MAP[hostname]) {
        domainLanguage = DOMAIN_LANGUAGE_MAP[hostname];
    }

    // Company language takes priority for logged-in users
    const companyLanguage = safeStorage.get('oogst_company_language');
    const localLanguage = safeStorage.get('oogst_preferred_language');
    const defaultLanguage = companyLanguage || localLanguage || ssrLanguage || domainLanguage || detectBrowserLanguage();

    // Store the language in localStorage for future visits
    if (defaultLanguage && typeof window !== 'undefined') {
        safeStorage.set('oogst_preferred_language', defaultLanguage);
    }

    const [translations, setTranslations] = useState(null);
    const [language, setLanguage] = useState(defaultLanguage);

    function switchLanguage(lang) {

        if (lang === language) {
            return
        }
        setLanguage(lang)
        console.log('switching language to', lang)
        if (safeStorage.set('oogst_preferred_language', lang)) {
            // Reload current page to apply the persisted language.
            window.location.reload();
        }
    }

    // Load translations on mount and when language changes
    useEffect(() => {
        api.getAllTranslations().then((translations) => {
            console.log(language)
            setTranslations(translations[language])
        })
    }, [language]);


    return (
        <TranslationContext.Provider value={{translations, setTranslations, language, switchLanguage}}>
            {children}
        </TranslationContext.Provider>
    );
};

export const useTranslationState = () => {
    const context = useContext(TranslationContext);
    if (!context) throw new Error("useTranslationState must be used within a TranslationProvider");
    return context;
};

export const useTranslations = () => {
    const {translations, setTranslations, language, switchLanguage} = useTranslationState();
    const missingKeysRef = useRef(new Set());

    const translate = (key) => {
        if (!translations) {
            return key;
        }
        if (translations[key]) {
            return translations[key];
        }

        // Track missing keys without causing re-render during render phase
        if (!missingKeysRef.current.has(key)) {
            missingKeysRef.current.add(key);
            // Report missing translation asynchronously
            Promise.resolve().then(() => {
                api.addMissingTranslation(key);
            });
        }

        return key;
    };

    // Update translations with missing keys after render
    useEffect(() => {
        if (missingKeysRef.current.size > 0 && translations) {
            const missingKeys = Array.from(missingKeysRef.current);
            missingKeysRef.current.clear();
            setTranslations(currentTranslations => {
                if (!currentTranslations) {
                    return currentTranslations;
                }

                let changed = false;
                const newTranslations = {...currentTranslations};
                missingKeys.forEach(key => {
                    if (!newTranslations[key]) {
                        newTranslations[key] = key;
                        changed = true;
                    }
                });
                return changed ? newTranslations : currentTranslations;
            });
        }
    }, [translations, setTranslations]);

    return {
        translate,
        language,
        switchLanguage
    };
};